import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { Badge, Button, Card, CardBody, CardHeader, Spinner, Stub, Toast } from "@/components/ui"
import { formatUSD } from "@/lib/utils"
import type { MyUsageOut } from "@/lib/types"

export const Route = createFileRoute("/_app/usage")({
  component: UsagePage,
})

// Server-supported periods are today | 7d | 30d | all (see app/api/me.py).
const PERIODS = ["today", "7d", "30d", "all"] as const

function UsagePage() {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>("30d")
  const usage = useQuery({
    queryKey: ["my-usage", period],
    queryFn: () => api<MyUsageOut>("/api/me/usage", { query: { period } }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Usage</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5">Where your tokens go. Updated live.</p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <Button key={p} size="sm" variant={p === period ? "default" : "ghost"} onClick={() => setPeriod(p)}>
              {p}
            </Button>
          ))}
        </div>
      </div>

      {usage.isLoading && <Spinner />}
      {usage.error && <Toast variant="destructive">{(usage.error as Error).message}</Toast>}

      {usage.data && (
        <>
          <Card>
            <CardBody>
              <div className="text-3xl font-semibold tabular-nums">
                {formatUSD(usage.data.split.total_usd)}
                <span className="text-xs text-muted-foreground ml-2 font-normal">{period}</span>
                {usage.data.has_unknown_pricing && (
                  <span className="ml-2"><Badge variant="warn">unknown pricing rows</Badge></span>
                )}
              </div>
              {usage.data.split.self_usd > 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {formatUSD(usage.data.split.platform_usd)} platform ·{" "}
                  {formatUSD(usage.data.split.self_usd)} billed to your own keys
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="By modality" />
            <CardBody>
              {usage.data.by_kind.length > 0 ? (
                <div className="grid gap-1.5 text-xs">
                  {usage.data.by_kind.map((k) => (
                    <Row
                      key={k.kind}
                      label={k.kind}
                      usd={k.usd}
                      sub={`${k.calls} calls · ${formatUSD(k.platform_usd)} platform / ${formatUSD(k.self_usd)} self`}
                    />
                  ))}
                </div>
              ) : <Stub title="No usage in this window" />}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="By model" />
            <CardBody>
              {usage.data.by_model.length > 0 ? (
                <div className="grid gap-1.5 text-xs">
                  {usage.data.by_model.map((m, i) => (
                    <Row
                      key={`${m.provider}-${m.model}-${m.billed_to}-${i}`}
                      label={`${m.provider} · ${m.model}`}
                      usd={m.usd}
                      sub={[
                        m.tokens_in > 0 || m.tokens_out > 0
                          ? `${m.tokens_in.toLocaleString()} in / ${m.tokens_out.toLocaleString()} out`
                          : null,
                        m.units > 0 && m.units_unit ? `${m.units.toLocaleString()} ${m.units_unit}` : null,
                        m.billed_to === "user" ? "billed to your key" : null,
                        m.unknown_pricing ? "unknown pricing" : null,
                      ].filter(Boolean).join(" · ")}
                    />
                  ))}
                </div>
              ) : <Stub title="No model usage" />}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="By day" />
            <CardBody>
              {usage.data.by_day.length > 0 ? (
                <div className="grid gap-1 text-[11px] font-mono">
                  {usage.data.by_day.map((d) => (
                    <div key={d.day} className="flex items-center justify-between">
                      <span>{d.day}</span>
                      <span className="tabular-nums">{formatUSD(d.usd)}</span>
                    </div>
                  ))}
                </div>
              ) : <Stub title="No daily breakdown yet" />}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  )
}

function Row({ label, usd, sub }: { label: string; usd: number; sub: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-foreground/[0.04] p-2.5">
      <div className="min-w-0">
        <div className="text-foreground truncate">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      </div>
      <div className="tabular-nums font-medium">{formatUSD(usd)}</div>
    </div>
  )
}
